import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { CheckCircle, XCircle, AlertTriangle, Camera, Keyboard, Star } from 'lucide-react';
import { verifyToken } from '../../services/guardService';
import { useOccupancy } from '../../context/OccupancyContext';
import '../../styles/ScanPage.css';

const STATUS_CONFIG = {
  valid:   { bg: '#22c55e', label: 'ACCÈS AUTORISÉ',  Icon: CheckCircle },
  expired: { bg: '#ef4444', label: 'TOKEN EXPIRÉ',    Icon: XCircle },
  used:    { bg: '#f59e0b', label: 'DÉJÀ UTILISÉ',   Icon: AlertTriangle },
  invalid: { bg: '#ef4444', label: 'TOKEN INVALIDE',  Icon: XCircle },
  revoked: { bg: '#ef4444', label: 'TOKEN RÉVOQUÉ',   Icon: XCircle },
};

export default function ScanPage() {
  const navigate = useNavigate();
  const { count, refresh } = useOccupancy();

  const [result, setResult]         = useState(null);
  const [loading, setLoading]       = useState(false);
  const [mode, setMode]             = useState('camera'); // 'camera' | 'manual'
  const [manualInput, setManualInput] = useState('');

  const scannerRef  = useRef(null);
  const timerRef    = useRef(null);
  const inputRef    = useRef(null);
  const processingRef = useRef(false); // prevent double-scan

  const stopScanner = useCallback(async () => {
    const instance = scannerRef.current;
    scannerRef.current = null;

    if (!instance) return;

    try {
      await Promise.resolve(instance.stop());
    } catch {}

    try {
      await Promise.resolve(instance.clear());
    } catch {}
  }, []);

  const [cameras, setCameras] = useState([]);
  const [activeCameraId, setActiveCameraId] = useState(null);

  // ── camera scanner lifecycle ───────────────────────────────────────────────
  useEffect(() => {
    if (mode !== 'camera' || result) return;

    let html5Qrcode;
    let stopped = false;
    let isRunning = false;

    const startScanner = async () => {
      const el = document.getElementById('qr-reader');
      if (!el || stopped) return;

      try {
        html5Qrcode = new Html5Qrcode('qr-reader');
        scannerRef.current = html5Qrcode;

        let availableCameras = cameras;
        if (availableCameras.length === 0) {
          try {
            availableCameras = await Html5Qrcode.getCameras();
            if (availableCameras && availableCameras.length > 0) {
              setCameras(availableCameras);
            }
          } catch (camErr) {
            console.warn('Could not get cameras', camErr);
            return;
          }
        }

        if (!availableCameras || availableCameras.length === 0 || stopped) return;

        let targetCameraId = activeCameraId;
        if (!targetCameraId) {
          const backCam = availableCameras.find(c => /back|rear|environment/i.test(c.label));
          targetCameraId = backCam ? backCam.id : availableCameras[0].id;
          // Note: we don't call setActiveCameraId here to avoid re-triggering the effect
        }

        if (stopped) return;

        await html5Qrcode.start(
          targetCameraId,
          { fps: 10, qrbox: { width: 250, height: 250 } },
          (decodedText) => {
            if (!processingRef.current) {
              processingRef.current = true;
              handleScan(decodedText);
            }
          },
          () => {} // ignore per-frame decode errors
        );

        isRunning = true;
      } catch (err) {
        console.warn('Camera start error (ignored):', err);
      }
    };

    startScanner();

    return () => {
      stopped = true;
      if (isRunning) {
        stopScanner();
        return;
      }
      Promise.resolve(scannerRef.current?.clear()).catch(() => {});
      scannerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, result, activeCameraId]);

  const switchCamera = async () => {
    if (cameras.length < 2) return;
    let currentId = activeCameraId;
    if (!currentId) {
       const backCam = cameras.find(c => /back|rear|environment/i.test(c.label));
       currentId = backCam ? backCam.id : cameras[0].id;
    }
    const currentIndex = cameras.findIndex(c => c.id === currentId);
    const nextIndex = (currentIndex + 1) % cameras.length;
    
    setMode('switching');
    await stopScanner();
    setActiveCameraId(cameras[nextIndex].id);
    
    setTimeout(() => {
      setMode('camera');
    }, 300);
  };

  // ── auto-focus manual input ────────────────────────────────────────────────
  useEffect(() => {
    if (mode === 'manual' && !result && inputRef.current) {
      inputRef.current.focus();
    }
  }, [mode, result]);

  // ── auto-dismiss result after 4 s ─────────────────────────────────────────
  useEffect(() => {
    if (result) {
      timerRef.current = setTimeout(dismissResult, 4000);
      return () => clearTimeout(timerRef.current);
    }
  }, [result]);

  const dismissResult = () => {
    setResult(null);
    setManualInput('');
    processingRef.current = false;
  };

  const handleScan = useCallback(async (token) => {
    if (!token || loading) return;
    setLoading(true);
    try {
      const res = await verifyToken(token.trim());
      await stopScanner();
      setResult(res.data);
      if (res.data.status === 'valid') refresh();
    } catch {
      await stopScanner();
      setResult({ status: 'invalid', visitorName: '', visitType: '' });
    } finally {
      setLoading(false);
    }
  }, [loading, refresh, stopScanner]);

  // ── scan screen ────────────────────────────────────────────────────────────
  return (
    <div className="scan-page">
        <div className="scan-header">
          <h1>Poste de sécurité</h1>
          <div className="occupancy-badge">
            Occupants actuels : <strong>{count}</strong>
          </div>
        </div>

        {/* Mode toggle */}
        <div className="scan-mode-toggle">
          <button
            className={`mode-btn ${mode === 'camera' ? 'active' : ''}`}
            onClick={() => setMode('camera')}
          >
            <Camera size={16} /> Caméra
          </button>
          <button
            className={`mode-btn ${mode === 'manual' ? 'active' : ''}`}
            onClick={() => setMode('manual')}
          >
            <Keyboard size={16} /> Saisie manuelle
          </button>
        </div>

        {mode === 'camera' ? (
          <div className="scan-camera-area">
            <p className="scan-instructions">Pointez la caméra vers le code QR du visiteur</p>
            <div id="qr-reader" className="qr-reader-container" />
            {cameras.length > 1 && (
              <button className="btn btn-secondary" onClick={switchCamera} style={{ marginTop: '1rem', width: '100%' }}>
                <Camera size={16} /> Changer de caméra ({cameras.length})
              </button>
            )}
          </div>
        ) : mode === 'manual' ? (
          <div className="scan-area">
            <p className="scan-instructions">Entrez ou collez le token QR manuellement</p>
            <input
              ref={inputRef}
              type="text"
              className="scan-input"
              value={manualInput}
              onChange={e => setManualInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleScan(manualInput)}
              placeholder="Token QR..."
            />
            <button
              className="btn btn-scan"
              onClick={() => handleScan(manualInput)}
              disabled={!manualInput || loading}
              style={{ minHeight: '80px' }}
            >
              {loading ? 'Vérification...' : 'Vérifier'}
            </button>
          </div>
        ) : (
          <div className="scan-camera-area" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
            <p>Changement de caméra en cours...</p>
          </div>
        )}

        <div className="scan-actions">
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/guard/manual')}
            style={{ minHeight: '80px' }}
          >
            Entrée manuelle visiteur
          </button>
        </div>

        {result && (() => {
          const cfg = STATUS_CONFIG[result.status] || STATUS_CONFIG.invalid;
          const { Icon } = cfg;

          return (
            <div className="scan-result-fullscreen" style={{ backgroundColor: cfg.bg }}>
              <div className="scan-result-content">
                <Icon size={96} strokeWidth={1.5} color="#fff" className="scan-result-icon-svg" />
                <div className="scan-result-label">{cfg.label}</div>
                {result.isVip && (
                  <div className="scan-result-vip-badge"><Star size={14} style={{marginRight: '4px', verticalAlign: 'text-bottom'}} /> VIP</div>
                )}
                {result.visitorName && (
                  <div className="scan-result-name">{result.visitorName}</div>
                )}
                {result.visitType && (
                  <div className="scan-result-type">{result.visitType.replace(/_/g, ' ')}</div>
                )}
                {result.inviterName && (
                  <div className="scan-result-type" style={{ marginTop: '8px', fontSize: '16px', opacity: 0.9 }}>
                    Invité par : <strong>{result.inviterName}</strong>
                  </div>
                )}
              </div>
              <button
                className="scan-result-dismiss"
                onClick={dismissResult}
                style={{ minHeight: '80px' }}
              >
                Retour au scan
              </button>
            </div>
          );
        })()}
      </div>
  );
}
